/** @jsxRuntime classic */
/** @jsx jsx */
import { css, jsx } from "@emotion/react";
import React, { FC } from "react";

import { EventInfo, getEventDisplayInfo } from "utility";
import type { Event } from "@spwn/types/firebase/firestore";
import { useI18n } from "hooks/i18n/i18n";
import { useGetEventList } from "hooks/event/useGetEventList";
import {
  PortalTopEventCard,
  PortalTopExternalGoodsCard,
} from "./PortalTopEventCard";
import { PortalTopSectionLayout } from "./PortalTop";
import { PortalTopLinkButton } from "./PortalTopLinkButton";

export const PortalTopGoodsFetcher: FC = () => {
  const LIST_COUNT = 3;

  const { data: events, isLoading } = useGetEventList({
    type: "openGoods",
    limit: LIST_COUNT,
  });

  return <PortalTopGoodsLogic events={events} isLoading={isLoading} />;
};

const PortalTopGoodsLogic: FC<{
  events?: Event[];
  isLoading?: boolean;
}> = ({ events, isLoading }) => {
  /**
   * @description
   * イベント一覧からカプセルイベントだけを取得する。
   */
  const capsuleGoodsList: EventInfo[] = (events ?? [])
    .flatMap((event) => getEventDisplayInfo(event))
    .reverse();

  return capsuleGoodsList.length === 0 || isLoading ? null : (
    <PortalTopGoods displayEvents={capsuleGoodsList} />
  );
};

const PortalTopGoods: FC<{
  displayEvents: EventInfo[];
}> = ({ displayEvents }) => {
  const { t } = useI18n();
  const styles = archiveListStyles();

  return (
    <PortalTopSectionLayout
      heading={{
        mainText: "GOODS",
        // eslint-disable-next-line no-restricted-syntax -- i18n 未対応
        subText: "グッズ",
      }}
    >
      <section>
        <div css={styles.layout}>
          {displayEvents.map((event, index) => {
            const { goodsUrl } = event;

            if (goodsUrl) {
              return (
                <React.Fragment key={`${event.eventId}_${index}`}>
                  <PortalTopExternalGoodsCard
                    href={goodsUrl}
                    // eslint-disable-next-line no-restricted-syntax -- i18n 未対応
                    tag={"販売中"}
                    tagColor={"#00C2AD"}
                    thumbnail={event.thumbnail}
                    date={event.eventOpenDateFull}
                    title={event.eventTitle}
                  />
                </React.Fragment>
              );
            }

            return (
              <React.Fragment key={`${event.eventId}_${index}`}>
                <PortalTopEventCard
                  href={`/events/${event.eventId}/goods`}
                  // eslint-disable-next-line no-restricted-syntax -- i18n 未対応
                  tag={"販売中"}
                  tagColor={"#00C2AD"}
                  thumbnail={event.thumbnail}
                  date={event.eventOpenDateFull}
                  title={event.eventTitle}
                />
              </React.Fragment>
            );
          })}
        </div>

        <PortalTopLinkButton
          href={`/goods`}
          label={t("event.top.archiveEventList")}
        />
      </section>
    </PortalTopSectionLayout>
  );
};

const archiveListStyles = () => {
  return {
    root: css``,
    layout: css`
      display: grid;
      grid-template-columns: 1fr 1fr;
      gap: 16px;
      margin-bottom: 20px;
      @media screen and (min-width: 768px) {
        grid-template-columns: 1fr 1fr 1fr;
        gap: 28px;
      }
    `,
    item: css`
      background-color: #fff;
      border-radius: 8px;
      overflow: hidden;
    `,
    thumbnail: css`
      position: relative;
      .tag {
        position: absolute;
        top: 0;
        right: 0;
        padding: 4px 10px;
        color: #ff5f96;
        font-size: 14px;
        font-weight: bold;
        background-color: rgba(0, 0, 0, 0.7);
      }
      img {
        width: 100%;
      }
    `,
    info: css`
      padding: 16px;
      font-weight: bold;
      line-height: 1.5em;
      .date {
        margin-bottom: 12px;
        @media screen and (min-width: 768px) {
          margin-bottom: 8px;
        }
      }
      .title {
        line-height: 1.5em;
      }
    `,
  };
};
